import AuthInput from "@/components/auth/auth-input";
import FormInput from "@/components/form-input";
import GoogleSignInButton from "@/components/auth/google-sign-in-button";
import Button from "@/components//button";
import OrLineSeparator from "./or-line-separator";
import { useEffect, useState } from "react";
import axios from "axios";
import Alert from "../alert";
import { signIn } from "next-auth/react";
import classNames from "classnames";
import {
  validateEmail,
  validatePasswordStrength,
  PASSWORD_STRENGTH_MESSAGE,
} from "@/lib/validation";
import TagManager from "react-gtm-module";
import Link from "next/link";
import { useRouter } from "next/router";
import CarouselGoogleReview from "@/components/carousel-google-review";

export default function RegisterForm({ pageContent }) {
  const router = useRouter();

  const [formStep, setFormStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const [isFirstNameValid, setIsFirstNameValid] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    const user = sessionStorage.getItem("login_@");
    if (user) {
      setEmail(user);
    }
  }, [router.query]);

  const handleEmailBlur = (email) => {
    sessionStorage.setItem("login_@", email);
    setIsEmailValid(validateEmail(email));
    setIsEmailTouched(true);
  };

  const handleContinue = async (event) => {
    event.preventDefault();
    handleEmailBlur(email);

    if (validateEmail(email)) {
      setFormStep(2);
      const data = {
        email: email,
        authProvider: "credentials",
      };
      axios.post("/api/save-user-tnc-acceptance", data);
    }
  };

  const checkIsFirstNameValid = (event) => {
    setIsFirstNameValid(event.target.value !== "");
  };

  const checkIsPasswordValid = (event) => {
    setIsPasswordValid(validatePasswordStrength(event.target.value));
  };

  const handleSubmit = async (event) => {
    setIsFirstNameValid(firstName !== "");
    setIsPasswordValid(validatePasswordStrength(password));
    event.preventDefault();

    const honeyPotValue = document.querySelector("input[name='url']").value;
    if (honeyPotValue !== "") {
      return;
    }

    if (isEmailValid && validatePasswordStrength(password)) {
      try {
        setIsSubmitting(true);
        setError("");
        setShowLoginPrompt(false);
        const data = {
          firstName: firstName,
          lastName: lastName,
          email: email,
          password: password,
        };
        const response = await axios.post("/api/register", data);
        if (response.status === 200) {
          // GTM
          if (
            process.env.NEXT_PUBLIC_ENV === "production" &&
            !process.env.NEXT_PUBLIC_CI
          ) {
            TagManager.dataLayer({
              dataLayer: { event: "signUp" },
            });
          }

          signIn("credentials", {
            username: email,
            password: password,
            callbackUrl: "/dashboard/setup",
          });
        }
      } catch (err) {
        if (err.response?.data === "ER_DUP_ENTRY") {
          setError("A user with this email already exists.");
          setShowLoginPrompt(true);
        } else {
          setError("An error occured. Please try again.");
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div
      style={{
        backgroundImage: `url("/images/mountain_illus.svg")`,
        backgroundPosition: "bottom",
      }}
      className="bg-cover min-h-[90vh]"
    >
      <div className="flex flex-wrap justify-center max-w-7xl 2xl:max-w-screen-2xl mx-auto">
        <div
          className={classNames(
            "w-11/12 md:w-5/6 lg:w-7/12 min-h-[90vh] bg-white/80",
            "py-10 px-8 sm:py-12 sm:px-16 md:py-14 md:px-28 lg:px-32",
            "flex flex-col justify-center items-center",
            "text-center text-sm 2xl:text-base"
          )}
        >
          <div className="h-[96%] sm:h-[520px] 2xl:h-[530px] flex flex-col">
            <form method="post" onSubmit={handleSubmit} className="w-full">
              {/* form step 1 */}
              {formStep === 1 && (
                <div
                  className={classNames(
                    "flex flex-col items-center space-y-10"
                  )}
                >
                  <div>
                    <h1 className="text-2xl md:text-3xl 2xl:text-4xl font-bold text-light-purple">
                      {"Let's get started"}
                    </h1>
                    <p className="text-sm 2xl:text-base text-subheading-gray mt-4">
                      {
                        "Get access to exclusive rates for the best shipping services, integrated seamlessly for your convenience."
                      }
                    </p>
                  </div>
                  <div className="w-full sm:max-w-[420px] sm:min-w-[320px] sm:w-[80%] pb-2">
                    <FormInput
                      type="email"
                      name="username"
                      id="username"
                      placeholder="Email"
                      className="bg-white/50"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      onBlur={(event) => handleEmailBlur(event.target.value)}
                      isValid={isEmailValid}
                      showValidation={isEmailTouched}
                      warnText={
                        email.length > 0
                          ? "Please enter a valid email"
                          : "Required"
                      }
                    />
                  </div>
                  <Button
                    type="submit"
                    name="continue"
                    className={classNames(
                      "min-w-[240px] py-3",
                      formStep === 1 ? "block" : "hidden"
                    )}
                    onClick={handleContinue}
                  >
                    {"Continue"}
                  </Button>
                </div>
              )}

              {/* form step 2 */}
              {formStep === 2 && (
                <div
                  className={classNames(
                    "flex flex-col items-center space-y-10"
                  )}
                >
                  <h1 className="text-2xl md:text-3xl font-bold text-light-purple">
                    {"Almost there!"}
                  </h1>
                  <div className="grid grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-6 text-left">
                    {/* spam-protection */}
                    <label className="hidden" htmlFor="url">
                      <input
                        type="url"
                        id="url"
                        name="url"
                        className="hidden"
                        aria-hidden="true"
                      />
                    </label>
                    <AuthInput
                      type="text"
                      name="firstname"
                      id="firstname"
                      labelHidden={false}
                      placeholder="First Name"
                      className=""
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      onBlur={checkIsFirstNameValid}
                      toWarn={!isFirstNameValid}
                      warnText="Required"
                    />
                    <AuthInput
                      required={false}
                      type="text"
                      name="lastname"
                      id="lastname"
                      className=""
                      labelHidden={false}
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                    />
                    <div className="col-span-2">
                      <AuthInput
                        type="password"
                        name="password"
                        id="password"
                        labelHidden={false}
                        placeholder="Password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        onBlur={checkIsPasswordValid}
                        toWarn={!isPasswordValid}
                        warnText={PASSWORD_STRENGTH_MESSAGE}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2 pt-6">
                    <Button
                      type="submit"
                      name="signup"
                      className="min-w-[240px] py-3"
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {"Sign Up"}
                    </Button>

                    <Button
                      type="button"
                      className="min-w-[240px] py-3"
                      variant="alternate"
                      onClick={() => setFormStep(1)}
                    >
                      {"Back"}
                    </Button>
                  </div>
                  <div>
                    {error && (
                      <Alert message={error} className="my-0 text-vsm" />
                    )}
                    {showLoginPrompt && (
                      <p className="font-medium text-vsm text-subheading-gray">
                        {"Already have an account? "}
                        <Link href="/sign-in">
                          <a className="underline text-light-purple hover:text-dark-purple">
                            {"Log back in here"}
                          </a>
                        </Link>
                        {"."}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </form>

            <div
              className={classNames(
                "flex flex-col items-center w-full",
                formStep == 1 ? "block" : "hidden"
              )}
            >
              <OrLineSeparator />
              <div className="text-center">
                <GoogleSignInButton callbackUrl="/dashboard/setup" />
              </div>
              <p className="mt-6 italic text-vsm text-subheading-gray">
                {"By continuing, you agree to our "}
                <Link href="/terms-of-use">
                  <a
                    target="_blank"
                    className="text-light-purple hover:text-dark-purple "
                  >
                    {"terms and conditions"}
                  </a>
                </Link>
                {"."}
              </p>
            </div>
          </div>
        </div>

        <div className="hidden lg:block w-full grow lg:w-auto">
          <div className="flex flex-col justify-center items-center h-full">
            <CarouselGoogleReview
              reviews={pageContent.segments[0].blocks}
              showButtons={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
